import { useToast } from "vue-toastification";
import store from "/@/store";
import { ref } from "vue";
import { LocationQuery, LocationQueryValue } from "vue-router";
import type { FileUpload, FileValidationResult } from "/@/types/FileUpload.type";
import type { ClaimDocumentRequests, SavedApplication } from "/@/types/Claims.type";
import {
  NOTHING_KNOWN,
  OpenCounts,
  parseOpenCounts,
} from "/@/document-request/open-counts-logic";

export default function useClaims() {
  const toast = useToast();
  const claims = ref<any>(null);
  const savedApplications = ref([] as any[]);
  const savedApplication = ref<SavedApplication | null>(null);
  const loadingSubmitted = ref(false);
  const loadingSaved = ref(false);

  const getCurrentUserClaims = async (filters: {
    page_size: number;
    page_number: number;
  }) => {
    try {
      loadingSubmitted.value = true;
      const res = await store.dispatch("claims/getCurrentUserClaims", filters);
      loadingSubmitted.value = false;

      claims.value = res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      loadingSubmitted.value = false;

      return Promise.reject(err);
    }
  };

  const getFuneralHomes = async () => {
    try {
      const res = await store.dispatch("claims/getFuneralHomes");

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const uploadApplicationFiles = async (applicationData: {
    applicationType: string;
    id: number;
    files: any;
  }) => {
    try {
      const res = await store.dispatch(
        "claims/uploadApplicationFiles",
        applicationData
      );

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const uploadSavedApplicationFiles = async (applicationData: {
    applicationType: string;
    id: number;
    files: any;
  }) => {
    try {
      const res = await store.dispatch(
        "claims/uploadSavedApplicationFiles",
        applicationData
      );

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const getApplicationDocumentTypes = async (applicationType: string) => {
    try {
      const res = await store.dispatch(
        "claims/getApplicationDocumentTypes",
        applicationType
      );

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const deleteApplication = async (
    applicationId: number,
    applicationType: string
  ) => {
    try {
      const res = await store.dispatch("claims/deleteApplication", {
        applicationId,
        applicationType,
      });

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const saveApplication = async (data: any) => {
    try {
      const res = await store.dispatch("claims/saveApplication", data);

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const updateSavedApp = async (payload: { applicationId: any; data: any }) => {
    try {
      const res = await store.dispatch("claims/updateSavedApp", payload);

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const getSavedApplications = async () => {
    try {
      loadingSaved.value = true;

      const res = await store.dispatch("claims/getSavedApplications");

      loadingSaved.value = false;

      savedApplications.value = res;

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      loadingSaved.value = false;

      return Promise.reject(err);
    }
  };

  const getSavedApplication = async (
    id: number | string | LocationQueryValue[]
  ) => {
    try {
      const res = await store.dispatch("claims/getSavedApplication", id);

      savedApplication.value = res;

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const deleteSavedApplication = async (applicationId: number) => {
    try {
      const res = await store.dispatch(
        "claims/deleteSavedApplication",
        applicationId
      );

      toast.success("Saved application deleted.");

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const deleteSavedFile = async (payload: {
    applicationId: any;
    documentType: string;
  }) => {
    try {
      const res = await store.dispatch("claims/deleteSavedFile", payload);

      toast.success("Saved file deleted.");

      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);

      return Promise.reject(err);
    }
  };

  const filterOptionalDocuments = (documents: FileUpload[], optionals: string[] | String[]) => {
    return documents.forEach((file: FileUpload) => {
      if (optionals.includes(file.title)) {
        file.required = false;
      } else {
        file.required = true;
      }
    });
  };

  const getApplicationById = async (
    applicationId: number | string,
    type: string
  ) => {
    try {
      const res = await store.dispatch("claims/getApplicationById", {
        applicationId,
        type,
      });
      return res;
    } catch (err: any) {
      const errorMessage = err.response?.data?.description ||
                           err.response?.data?.message ||
                           err.message ||
                           'An unexpected error occurred. Please try again.';
      toast.error(errorMessage);
      return false;
    }
  };

  /**
   * Documents NIB asked this claimant for (DR-08). THROWS on failure, no toast:
   * ActionRequired renders its own error state (a 503 shows the API's
   * "Requests temporarily unavailable" wording with a retry).
   */
  const getDocumentRequests = async (
    applicationId: number | string
  ): Promise<ClaimDocumentRequests> => {
    const res = await store.dispatch("claims/getDocumentRequests", applicationId);
    return {
      requests: Array.isArray(res?.requests) ? res.requests : [],
      catalogue: Array.isArray(res?.catalogue) ? res.catalogue : [],
    };
  };

  /**
   * Documents outstanding across ALL of this citizen's claims, in one call.
   *
   * Never throws. A network failure is indistinguishable to the citizen from
   * the admin API being down, and both mean the same thing: say nothing rather
   * than claim there is nothing outstanding.
   */
  const getOpenDocumentRequestCounts = async (): Promise<OpenCounts> => {
    try {
      const res = await store.dispatch("claims/getOpenDocumentRequestCounts");
      return parseOpenCounts(res);
    } catch (err) {
      return NOTHING_KNOWN;
    }
  };

  const validateRequiredFiles = (files: FileUpload[]): FileValidationResult => {
    const missing: string[] = [];

    files.forEach((file: FileUpload) => {
      if (file.required && !file.value) {
        missing.push(file.title);
      }
    });

    return { valid: missing.length === 0, missing };
  };

  return {
    getFuneralHomes,
    getDocumentRequests,
    getOpenDocumentRequestCounts,
    uploadApplicationFiles,
    uploadSavedApplicationFiles,
    getApplicationDocumentTypes,
    deleteApplication,
    getCurrentUserClaims,
    claims,
    saveApplication,
    getSavedApplications,
    savedApplications,
    loadingSubmitted,
    loadingSaved,
    getSavedApplication,
    deleteSavedApplication,
    filterOptionalDocuments,
    savedApplication,
    deleteSavedFile,
    updateSavedApp,
    getApplicationById,
    validateRequiredFiles,
  };
}
